import { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router'
import { AccountCircleOutlined, BookmarkBorderOutlined, CloseOutlined, InfoOutlined, NewReleasesOutlined, SearchOutlined } from '@mui/icons-material'
import { AppBar, Backdrop, Box, Divider, Grid, IconButton, ListItemIcon, Menu, MenuItem, Toolbar, Typography } from '@mui/material'

import meshifyCentralLogo from '../../images/meshify-central-logo.svg'
import { useAuthentication } from '../../providers/auth-provider'
import useSharedStyles from '../../utils/shared.styles'
import Search from '../search'

import AboutModal from './about-modal'
import useStyles from './index.styles'

const Header = () => {
  const classes = useStyles
  const sharedClasses = useSharedStyles
  const navigate = useNavigate()
  const { ownUser, handleLogout } = useAuthentication()

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [showSearch, setShowSearch] = useState<boolean>(false)
  const [showAboutDialog, setShowAboutDialog] = useState<boolean>(false)

  const menuOpen = Boolean(menuAnchor)

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleAboutClick = () => {
    setShowAboutDialog(true)
    handleMenuClose()
  }

  const handleBookmarkedClick = () => {
    navigate('/bookmarks')
    handleMenuClose()
  }

  const handleReleaseNotesClick = () => {
    navigate('/release-notes')
    handleMenuClose()
  }

  const handleProfileClick = () => {
    navigate(`/profile`)
    handleMenuClose()
  }

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <AppBar position="fixed" sx={classes.header} elevation={0}>
          <Toolbar sx={classes.toolbar}>
            <Grid container={true} size="grow" sx={sharedClasses.centerContentVertical}>
              <Grid
                size={{
                  xs: 8,
                  md: 8,
                  xl: 10,
                }}
              >
                <RouterLink to="/dashboard" style={classes.headerLogoLink}>
                  <img src={meshifyCentralLogo} alt="logo" style={classes.headerLogo} aria-label="Header Logo" />
                </RouterLink>
              </Grid>

              <Grid
                container={true}
                size={{
                  xs: 4,
                  md: 4,
                  xl: 2,
                }}
                sx={{
                  justifyContent: 'flex-end',
                }}
              >
                <Grid>
                  <IconButton onClick={() => setShowSearch(true)} aria-label="Search button">
                    <SearchOutlined fontSize="large" color="primary" />
                  </IconButton>
                </Grid>
                <Grid>
                  <IconButton onClick={handleMenuClick} aria-label="Account button" data-cy="account-button">
                    <AccountCircleOutlined fontSize="large" color="primary" />
                  </IconButton>
                </Grid>

                <Backdrop open={showSearch} onClick={() => setShowSearch(false)} sx={classes.backdrop}>
                  <Typography variant="h4" sx={classes.backdropSearchTitle}>
                    What are you looking for?
                  </Typography>
                  <Box sx={classes.backdropSearchContainer} onClick={(e) => e.stopPropagation()}>
                    <Search closeOverlay={() => setShowSearch(false)} />
                  </Box>
                  <IconButton onClick={() => setShowSearch(false)} aria-label="Close search" sx={classes.backdropCloseIconButton}>
                    <CloseOutlined sx={classes.backdropCloseIcon} />
                  </IconButton>
                </Backdrop>

                <Menu
                  anchorEl={menuAnchor}
                  id="account-menu"
                  open={menuOpen}
                  onClose={handleMenuClose}
                  onClick={handleMenuClose}
                  slotProps={{ paper: { sx: classes.menuPaper } }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  {ownUser && (
                    <MenuItem sx={classes.menuUserInfo} disabled={true}>
                      <Typography variant="body1" className="fs-exclude">
                        {ownUser.information?.first} {ownUser.information?.last}
                      </Typography>
                      <Typography variant="body2" className="fs-exclude" sx={classes.menuEmail}>
                        {ownUser.email}
                      </Typography>
                    </MenuItem>
                  )}

                  <Divider />

                  <MenuItem onClick={handleProfileClick}>
                    <ListItemIcon>
                      <AccountCircleOutlined />
                    </ListItemIcon>
                    <Typography variant="body2">Profile</Typography>
                  </MenuItem>

                  <MenuItem onClick={handleBookmarkedClick}>
                    <ListItemIcon>
                      <BookmarkBorderOutlined />
                    </ListItemIcon>
                    <Typography variant="body2">Bookmarked</Typography>
                  </MenuItem>

                  <MenuItem onClick={handleAboutClick}>
                    <ListItemIcon>
                      <InfoOutlined />
                    </ListItemIcon>
                    <Typography variant="body2">About Central</Typography>
                  </MenuItem>

                  <MenuItem onClick={handleReleaseNotesClick}>
                    <ListItemIcon>
                      <NewReleasesOutlined />
                    </ListItemIcon>
                    <Typography variant="body2">What's new</Typography>
                  </MenuItem>

                  <Divider />

                  <MenuItem onClick={() => handleLogout()}>
                    <Typography variant="buttonSmall">Log out</Typography>
                  </MenuItem>
                </Menu>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      </Box>
      <Toolbar />
      <AboutModal open={showAboutDialog} setOpen={setShowAboutDialog} />
    </>
  )
}

export default Header
